import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatDate } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = () => ({
  period: '',
  average_grade: '',
  teacher_comment: '',
  academy_comment: '',
  subjects: [],
});

// Phase 75.1 — Normalise course_completion_percentage: blank/empty → null
// (never silently 0). Returns the cleaned subjects array ready for POST/PUT.
const cleanSubjectsForSubmit = (subjects) => (subjects || []).map(s => {
  const raw = s.course_completion_percentage;
  let pct = null;
  if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
    const n = Number(raw);
    pct = Number.isFinite(n) ? n : null;
  }
  return {
    subject_name: s.subject_name || '',
    grade: s.grade || '',
    comment: s.comment || '',
    course_completion_percentage: pct,
  };
});

/**
 * Phase 73.A — profile-scoped Reports / Grades panel.
 *
 * When invoked with `athlete` + `profile` + `canEdit=true`, this panel allows
 * creating new academic reports tied to the selected academic_profile_id.
 * When invoked without those props (e.g. the General / Unassigned bucket),
 * it falls back to a pure read-only list — preserving legacy behaviour.
 *
 * Phase 72.C polish — reuses the existing /api/subjects predefined list so
 * the new profile-scoped report modal does not have a parallel subject system.
 * Free-text fallback is preserved for old reports whose subject_name is not
 * (or no longer is) in the predefined list.
 */
const AcademicReportsPanel = ({ reports, athlete, profile, canEdit = false, onChanged }) => {
  const [dlg, setDlg] = useState({ open: false, editing: null, data: blank() });
  const [subjectInput, setSubjectInput] = useState({ subject_name: '', grade: '', comment: '', course_completion_percentage: '' });
  const [predefinedSubjects, setPredefinedSubjects] = useState([]);

  // Phase 75.1 — surface the optional Completion % input only when the
  // active academic profile has track_course_completion: true. Read-only
  // panels (General/Unassigned) inherit `profile=undefined` → field hidden.
  const tracksCompletion = !!profile?.track_course_completion;

  // Load predefined subjects once when the panel mounts (same source as the
  // legacy "Full Academic Reports Archive" subject system).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/subjects`)
      .then(r => { if (!cancelled) setPredefinedSubjects(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { /* silent — modal falls back to free-text */ });
    return () => { cancelled = true; };
  }, []);

  const setData = (patch) => setDlg(s => ({ ...s, data: { ...s.data, ...patch } }));
  const canCreate = canEdit && athlete?.id && profile?.id;

  const openAdd = () => {
    setSubjectInput({ subject_name: '', grade: '', comment: '', course_completion_percentage: '' });
    setDlg({ open: true, editing: null, data: blank() });
  };

  const openEdit = (report) => {
    setSubjectInput({ subject_name: '', grade: '', comment: '', course_completion_percentage: '' });
    setDlg({
      open: true,
      editing: report,
      data: {
        period: report.period || '',
        average_grade: report.average_grade || '',
        teacher_comment: report.teacher_comment || '',
        academy_comment: report.academy_comment || '',
        subjects: (report.subjects || []).map(s => ({
          subject_name: s.subject_name || '',
          grade: s.grade || '',
          comment: s.comment || '',
          // Render existing value as a string (so the input is controlled).
          // Blank/null stays blank — we never silently coerce to 0.
          course_completion_percentage: (s.course_completion_percentage === null || s.course_completion_percentage === undefined)
            ? ''
            : String(s.course_completion_percentage),
        })),
      },
    });
  };

  const addSubject = () => {
    const name = subjectInput.subject_name.trim();
    if (!name) { toast.error('Subject name required'); return; }
    if (dlg.data.subjects.find(s => s.subject_name === name)) {
      toast.error('Subject already added'); return;
    }
    // Phase 75.1 — validate completion % if tracking is enabled.
    if (tracksCompletion && subjectInput.course_completion_percentage !== '') {
      const n = Number(subjectInput.course_completion_percentage);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        toast.error('Completion % must be a number between 0 and 100'); return;
      }
    }
    setData({ subjects: [...dlg.data.subjects, { ...subjectInput, subject_name: name }] });
    setSubjectInput({ subject_name: '', grade: '', comment: '', course_completion_percentage: '' });
  };

  const removeSubjectAt = (idx) => {
    setData({ subjects: dlg.data.subjects.filter((_, i) => i !== idx) });
  };

  const updateSubjectAt = (idx, field, value) => {
    const next = [...dlg.data.subjects];
    next[idx] = { ...next[idx], [field]: value };
    setData({ subjects: next });
  };

  const submit = async () => {
    if (!dlg.data.period.trim()) { toast.error('Period is required (e.g., Term 1 2026)'); return; }
    // Phase 75.1 — validate per-row completion % when tracking is enabled.
    if (tracksCompletion) {
      for (const s of dlg.data.subjects) {
        const raw = s.course_completion_percentage;
        if (raw === null || raw === undefined || String(raw).trim() === '') continue; // null/blank ok
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          toast.error(`Completion % for "${s.subject_name}" must be between 0 and 100`); return;
        }
      }
    }
    const cleanedSubjects = cleanSubjectsForSubmit(dlg.data.subjects);
    try {
      if (dlg.editing) {
        await axios.put(`${API}/academic-reports/${dlg.editing.id}`, {
          ...dlg.data,
          subjects: cleanedSubjects,
          academic_profile_id: profile?.id || dlg.editing.academic_profile_id || null,
        });
        toast.success('Academic report updated');
      } else {
        await axios.post(`${API}/academic-reports`, {
          athlete_id: athlete.id,
          academic_profile_id: profile.id,
          ...dlg.data,
          subjects: cleanedSubjects,
        });
        toast.success('Academic report created');
      }
      setDlg({ open: false, editing: null, data: blank() });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save report');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this academic report?')) return;
    try {
      await axios.delete(`${API}/academic-reports/${id}`);
      toast.success('Academic report deleted');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-2" data-testid="academic-reports-list">
      {/* Phase 75.2 — clarify the relationship with the Full Academic Reports
          archive section below. Only show when this panel is being used as the
          profile-scoped working area (i.e. canCreate is true). */}
      {canCreate && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="programme-reports-helper">
          Create and manage reports for the selected academic profile.
        </p>
      )}

      {canCreate && (
        <div className="flex justify-end">
          <Button size="sm" onClick={openAdd} data-testid="add-report-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Report
          </Button>
        </div>
      )}

      {reports.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No reports for this section yet.</p>
      ) : reports.map(r => (
        <div key={r.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800" data-testid={`academic-report-${r.id}`}>
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.period}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                {r.average_grade && (
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">Avg Grade: {r.average_grade}</p>
                )}
                {/* Phase 75.1 — Only render Avg Completion when at least one
                    subject had a value (backend returns null otherwise — never 0). */}
                {(r.average_course_completion_percentage !== null && r.average_course_completion_percentage !== undefined) && (
                  <p className="text-xs text-sky-700 dark:text-sky-300" data-testid={`report-avg-completion-${r.id}`}>
                    Avg Completion: {r.average_course_completion_percentage}%
                  </p>
                )}
              </div>
              {r.subjects?.length ? (
                <p className="text-xs text-gray-500 mt-0.5">{r.subjects.length} subject{r.subjects.length === 1 ? '' : 's'}</p>
              ) : null}
              {r.teacher_comment && (
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2"><span className="font-medium">Teacher:</span> {r.teacher_comment}</p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[10px] text-gray-400 whitespace-nowrap mr-1">{formatDate(r.created_at)}</span>
              {canCreate && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-report-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(r.id)} data-testid={`delete-report-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dlg.editing ? 'Edit Report' : 'Add Report'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Period / Title *</Label>
                <Input
                  value={dlg.data.period}
                  onChange={(e) => setData({ period: e.target.value })}
                  placeholder="e.g., Term 1 2026"
                  data-testid="report-period-input"
                />
              </div>
              <div>
                <Label>Average Grade</Label>
                <Input
                  value={dlg.data.average_grade}
                  onChange={(e) => setData({ average_grade: e.target.value })}
                  placeholder="e.g., 85% or A-"
                  data-testid="report-average-grade-input"
                />
              </div>
            </div>
            <div>
              <Label>Teacher / Staff Comment</Label>
              <Textarea
                rows={3}
                value={dlg.data.teacher_comment}
                onChange={(e) => setData({ teacher_comment: e.target.value })}
                placeholder="Teacher's feedback..."
                data-testid="report-teacher-comment-input"
              />
            </div>
            <div>
              <Label>Academy Comment</Label>
              <Textarea
                rows={2}
                value={dlg.data.academy_comment}
                onChange={(e) => setData({ academy_comment: e.target.value })}
                placeholder="Internal academy note (optional)"
                data-testid="report-academy-comment-input"
              />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <Label className="block mb-2">Subjects / Grades</Label>
              {tracksCompletion && (
                <p className="text-[11px] text-sky-700 dark:text-sky-300 mb-2" data-testid="completion-tracking-hint">
                  This programme tracks Course Completion %. Leave the % field blank if not applicable — it will not be counted toward the average.
                </p>
              )}
              {dlg.data.subjects.length > 0 && (
                <div className="space-y-2 mb-3">
                  {dlg.data.subjects.map((s, idx) => {
                    // If the stored subject_name is not in the predefined list,
                    // keep it as a read-only "(legacy) <name>" item so old data
                    // is never lost. Editing falls back to a free-text input.
                    const inList = predefinedSubjects.some(p => p.name === s.subject_name);
                    const gridCols = tracksCompletion
                      ? 'grid-cols-[1fr_100px_110px_1.5fr_auto]'
                      : 'grid-cols-[1fr_100px_1.5fr_auto]';
                    return (
                      <div key={idx} className={`grid ${gridCols} gap-2 items-center`} data-testid={`subject-row-${idx}`}>
                        {inList || predefinedSubjects.length === 0 ? (
                          <Select value={s.subject_name} onValueChange={(v) => updateSubjectAt(idx, 'subject_name', v)}>
                            <SelectTrigger data-testid={`subject-select-${idx}`}><SelectValue placeholder="Select subject" /></SelectTrigger>
                            <SelectContent>
                              {predefinedSubjects.map(p => (
                                <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                              ))}
                              {!inList && s.subject_name && (
                                <SelectItem value={s.subject_name}>{s.subject_name} (legacy)</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={s.subject_name} onChange={(e) => updateSubjectAt(idx, 'subject_name', e.target.value)} placeholder="Subject" />
                        )}
                        <Input value={s.grade} onChange={(e) => updateSubjectAt(idx, 'grade', e.target.value)} placeholder="Grade" />
                        {tracksCompletion && (
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={s.course_completion_percentage ?? ''}
                            onChange={(e) => updateSubjectAt(idx, 'course_completion_percentage', e.target.value)}
                            placeholder="% (0–100)"
                            data-testid={`subject-completion-${idx}`}
                          />
                        )}
                        <Input value={s.comment} onChange={(e) => updateSubjectAt(idx, 'comment', e.target.value)} placeholder="Comment (optional)" />
                        <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeSubjectAt(idx)} data-testid={`remove-subject-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className={`grid ${tracksCompletion ? 'grid-cols-[1fr_100px_110px_1.5fr_auto]' : 'grid-cols-[1fr_100px_1.5fr_auto]'} gap-2 items-center`}>
                {predefinedSubjects.length > 0 ? (
                  <Select value={subjectInput.subject_name} onValueChange={(v) => setSubjectInput({ ...subjectInput, subject_name: v })}>
                    <SelectTrigger data-testid="subject-name-input"><SelectValue placeholder="Select subject" /></SelectTrigger>
                    <SelectContent>
                      {predefinedSubjects.map(p => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={subjectInput.subject_name}
                    onChange={(e) => setSubjectInput({ ...subjectInput, subject_name: e.target.value })}
                    placeholder="Subject name"
                    data-testid="subject-name-input"
                  />
                )}
                <Input
                  value={subjectInput.grade}
                  onChange={(e) => setSubjectInput({ ...subjectInput, grade: e.target.value })}
                  placeholder="Grade"
                  data-testid="subject-grade-input"
                />
                {tracksCompletion && (
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={subjectInput.course_completion_percentage}
                    onChange={(e) => setSubjectInput({ ...subjectInput, course_completion_percentage: e.target.value })}
                    placeholder="% (0–100)"
                    data-testid="subject-completion-input"
                  />
                )}
                <Input
                  value={subjectInput.comment}
                  onChange={(e) => setSubjectInput({ ...subjectInput, comment: e.target.value })}
                  placeholder="Comment (optional)"
                  data-testid="subject-comment-input"
                />
                <Button type="button" size="sm" variant="outline" onClick={addSubject} data-testid="add-subject-btn">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(s => ({ ...s, open: false }))}>Cancel</Button>
            <Button onClick={submit} data-testid="save-report-btn">{dlg.editing ? 'Save' : 'Create report'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademicReportsPanel;
